// =============================================================================
// app/src/componentes/CartaoKPI.tsx — o cartão de número grande do topo.
//
// O padrão certo já existia — em UMA tela (Caixa.tsx:375, com o comentário
// explicando): `text-base sm:text-2xl` + `whitespace-nowrap` + `tabular-nums`.
// "R$ 1.075,50" em três colunas de 375px não cabe em text-2xl fixo, e valor
// CORTADO num painel de dinheiro é pior que valor pequeno. Fiado, ContasPagar
// e SaudeNegocio continuavam estourando — cada um com a própria cópia.
//
// Canonizado em 23/08/2026. Adoção incremental: as telas tocadas pelas ondas
// A/B usam já; o resto migra em PRs de manutenção.
// =============================================================================
import type { ReactNode } from 'react';

/**
 * O CORTE VOLTOU POR OUTRO LADO, e a régua tinha de ser outra. O `text-base
 * sm:text-2xl` resolveu o caso de 2026-08 — três colunas a 375 px —, mas ele
 * olha a LARGURA DA TELA e o que corta é o COMPRIMENTO DO VALOR. Medido em
 * 28/09/2026 na /provisoes, a 794 px (uma folha A4): "R$ 1.139.854,39" pede
 * 185 px numa caixa de 149 e sai `R$ 1.139.854,3` — o número principal da tela
 * truncado, que é exatamente o que o cabeçalho acima diz ser pior que número
 * pequeno.
 *
 * Então o tamanho grande fica para quem cabe. Um valor curto ("R$ 1.075,50")
 * continua em `sm:text-2xl`; sete dígitos descem um ou dois degraus. A conta é
 * grosseira de propósito — dígito tabular tem largura fixa, e errar para menos
 * custa um número menor, enquanto errar para mais custa um número cortado.
 */
const DEGRAUS: Array<[number, string]> = [
  [11, 'sm:text-2xl'],
  [14, 'sm:text-xl'],
  [Infinity, 'sm:text-lg'],
];

const tamanhoDoValor = (valor: ReactNode): string => {
  const texto = typeof valor === 'string' || typeof valor === 'number' ? String(valor) : '';
  // Nó que não é texto (um ícone, um <span>) não se mede: fica no grande.
  if (texto === '') return DEGRAUS[0]![1];
  return DEGRAUS.find(([ate]) => texto.length <= ate)![1];
};

const TONS = {
  neutro: '',
  ok: 'text-ok',
  erro: 'text-erro',
  alerta: 'text-alerta',
} as const;

export default function CartaoKPI({ rotulo, valor, detalhe, tom = 'neutro', className = '', numero = false }: {
  rotulo: string;
  /** O número já formatado (brl, contagem…) — o cartão só apresenta. */
  valor: ReactNode;
  detalhe?: ReactNode;
  tom?: keyof typeof TONS;
  /** Para o cartão ocupar a linha toda no celular (col-span-2), por exemplo. */
  className?: string;
  /**
   * O valor em GEIST MONO, com os algarismos alinhados (Grafite, 29/10/2026).
   * Opt-in, tela a tela: o dígito mono é mais largo que o da Geist, e cada
   * tela que liga isto é medida a 375 px no WebKit antes — ligar em todas de
   * uma vez é como se corta "R$ 1.234,56" numa tela que ninguém abriu.
   */
  numero?: boolean;
}) {
  return (
    <div className={`cartao p-3 ${className}`}>
      <p className="rotulo">{rotulo}</p>
      <p className={`text-base whitespace-nowrap ${numero ? 'numero font-medium' : 'font-black tabular-nums'} ${tamanhoDoValor(valor)} ${TONS[tom]}`}>
        {valor}
      </p>
      {detalhe !== undefined && <p className="text-xs text-dim">{detalhe}</p>}
    </div>
  );
}
